import { SelectedFilters } from '@/components/FilterData/types';
import { MatchTypeRadio } from '@/components/Search/SearchControls/MatchTypeRadio';
import { MatchType } from '@/components/Search/types';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { Box, FormControl, RadioGroup } from '@mui/material';
import geometricMatchImage from './images/geometricMatch.png';
import partMatchImage from './images/partMatch.png';
import visualMatchImage from './images/visualMatch.png';
import metadataMatchImage from './images/metadataMatch.png';
import substitutesMatchImage from './images/substitutesMatch.png';
import { useCrossTenantGeometricSearch } from '@/hooks/api/crossTenant/assetsGeometricSearch/useCrossTenantGeometricSearch';
import { useTenants } from '@/services/tenants/useTenants';
import { useCrossTenantPartSearch } from '@/hooks/api/crossTenant/assetsPartSearch/useCrossTenantPartSearch';
import { useCrossTenantVisualSearch } from '@/hooks/api/crossTenant/assetsVisualSearch/useCrossTenantVisualSearch';
import { useCrossTenantMetadataSearch } from '@/hooks/api/crossTenant/metadataSearch/useCrossTenantMetadataSearch';
import { useAssetCompositeSearch } from '@/hooks/api/assets/assetCompositeSearch/useAssetCompositeSearch';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { BodyText } from '@/components/BodyText';

export const MatchTypeSelection = ({ selectedFilters }: { selectedFilters: SelectedFilters }) => {
  const {
    assetId,
    searchType,
    updateSearchParameters,
    minThreshold,
    sizeThreshold,
    searchQuery,
    selectedTenantId: assetTenantId,
  } = useSearchFilters();
  const { getTenantId } = useTenants();
  const currentTenantId = getTenantId();
  const { asset } = useGetAsset({ assetId });
  const { isMetadataSearchEnabled, isSuitableSubstitutesEnabled } = useFeatureFlags();

  const {
    data: geometricMatches,
    isLoading: isGeometricLoading,
    error: geometricError,
  } = useCrossTenantGeometricSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId: assetTenantId,
    minThreshold,
    selectedFilters,
    searchQuery,
    enabled: !!currentTenantId,
  });

  const {
    data: partMatches,
    isLoading: isPartLoading,
    error: partError,
  } = useCrossTenantPartSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId: assetTenantId,
    minThreshold,
    selectedFilters,
    searchQuery,
    enabled: !!currentTenantId,
  });

  const {
    data: visualMatches,
    isLoading: isVisualLoading,
    error: visualError,
  } = useCrossTenantVisualSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId: assetTenantId,
    searchQuery,
    selectedFilters,
    sizeThreshold,
    enabled: !!currentTenantId,
  });

  const {
    data: metadataMatches,
    isLoading: isMetadataLoading,
    error: metadataError,
  } = useCrossTenantMetadataSearch({
    assetId,
    assetTenantId: currentTenantId,
    searchTenantId: assetTenantId,
    searchQuery,
    selectedFilters,
    enabled: isMetadataSearchEnabled && !!assetId && !!currentTenantId,
  });

  const {
    data: substitutesMatches,
    isLoading: isSubstitutesLoading,
    error: substitutesError,
  } = useAssetCompositeSearch({
    assetId,
    tenantId: currentTenantId,
    searchQuery,
    selectedFilters,
    enabled: isSuitableSubstitutesEnabled && !!assetId,
  });

  const handleMatchTypeChange = (matchType: MatchType) => {
    if (searchType !== matchType) {
      updateSearchParameters({ searchType: matchType });
    }
  };

  return (
    <Box data-testid={SearchPageObjectModel.MatchTypeSelection}>
      <BodyText color='text.secondary'>Match Type</BodyText>
      <FormControl component='fieldset' fullWidth>
        <RadioGroup aria-label='match-type' name='match-type' value={searchType} sx={{ gap: 0 }}>
          <MatchTypeRadio
            value='geometric'
            label='Geometric'
            count={geometricMatches?.pageData?.total}
            imageSrc={geometricMatchImage}
            description='Geometrically similar parts'
            selected={searchType === 'geometric'}
            onChange={handleMatchTypeChange}
            disabled={asset?.type !== 'model'}
            isLoading={isGeometricLoading}
            error={geometricError}
          />
          <MatchTypeRadio
            value='part'
            label='Part'
            count={partMatches?.pageData?.total}
            imageSrc={partMatchImage}
            description='Assemblies containing similar parts'
            selected={searchType === 'part'}
            onChange={handleMatchTypeChange}
            disabled={asset?.type !== 'model'}
            isLoading={isPartLoading}
            error={partError}
          />
          <MatchTypeRadio
            value='visual'
            label='Visual'
            count={visualMatches?.pageData.total}
            imageSrc={visualMatchImage}
            description='Visually similar parts'
            selected={searchType === 'visual'}
            onChange={handleMatchTypeChange}
            isLoading={isVisualLoading}
            error={visualError}
          />
          {isMetadataSearchEnabled && (
            <MatchTypeRadio
              value='metadata'
              label='Metadata'
              count={metadataMatches?.pageData?.total}
              imageSrc={metadataMatchImage}
              description='Parts with similar metadata'
              selected={searchType === 'metadata'}
              onChange={handleMatchTypeChange}
              isLoading={isMetadataLoading}
              error={metadataError}
            />
          )}
          {isSuitableSubstitutesEnabled && (
            <MatchTypeRadio
              value='substitutes'
              label='Substitutes'
              count={substitutesMatches?.pageData?.total}
              imageSrc={substitutesMatchImage}
              description='Part substitutes'
              selected={searchType === 'substitutes'}
              onChange={handleMatchTypeChange}
              disabled={asset?.type !== 'model'}
              isLoading={isSubstitutesLoading}
              error={substitutesError}
              beta={true}
            />
          )}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};
